// PlaceWizardIntro - Motivierende Einführungsseite vor dem Ort-Vorschlag-Formular
// Erklärt was einen GoodPlace ausmacht und inspiriert mit Beispielen
// Design: Atmosphärisch mit Hintergrundbild und Glasmorphism

import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Armchair, Theater, Sparkles, BookOpen, Trees, Waves, ChevronDown } from 'lucide-react';

interface PlaceWizardIntroProps {
  onContinue: () => void;
  onCancel: () => void;
}

// Atmosphärische Hintergrundbilder - GoodPlace Stimmung
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1559925393-8be0ec4767c8?w=1600&q=80', // Cozy cafe with plants
  'https://images.unsplash.com/photo-1521017432531-fbd92d768814?w=1600&q=80', // Warm library atmosphere
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80', // Peaceful nature
  'https://images.unsplash.com/photo-1445116572660-236099ec97a0?w=1600&q=80', // Coffee shop warmth
];

// Beispiel-Orte für Inspiration
const EXAMPLE_PLACES = [
  {
    name: 'Monacensia im Hildebrandhaus',
    icon: BookOpen,
    goodFactor: 'Hier wird Kultur demokratisiert. Man darf in den Sesseln lümmeln – ohne Konsumzwang.',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80',
  },
  {
    name: 'Alter Nordfriedhof',
    icon: Trees,
    goodFactor: 'Der leiseste Ort in der lauten Maxvorstadt. Wo früher Trauer herrschte, feiert heute die Stille das Leben.',
    image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&q=80',
  },
  {
    name: 'Gans am Wasser',
    icon: Waves,
    goodFactor: 'Ein konsumfreier Safe Space am See. Nachhaltig gebaut, offen für alle.',
    image: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=400&q=80',
  },
];

// Die 3 Kernfragen
const CORE_QUESTIONS = [
  {
    icon: Armchair,
    emoji: '🪑',
    title: 'Darfst du einfach da sein?',
    description: 'Ohne etwas zu kaufen. Ohne Termin. Ohne Erklärung.',
    tag: '#anlasslos',
  },
  {
    icon: Theater,
    emoji: '🎭',
    title: 'Kannst du dort du selbst sein?',
    description: 'Nicht als Kunde, nicht als Chef – einfach als Mensch.',
    tag: '#rollenlos',
  },
  {
    icon: Sparkles,
    emoji: '✨',
    title: 'Gibt es dort Momente, die man nicht planen kann?',
    description: 'Zufallsbegegnungen. Überraschende Stille. Unverhoffte Schönheit.',
    tag: '#unverfuegbares_glueck',
  },
];

export function PlaceWizardIntro({ onContinue, onCancel }: PlaceWizardIntroProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Rotate background image every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight * 0.85, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-900">
      {/* HERO SECTION - Fullscreen mit Hintergrundbild */}
      <section className="relative h-screen min-h-[600px] w-full overflow-hidden">
        {/* Background Images with Crossfade */}
        <div className="absolute inset-0">
          {HERO_IMAGES.map((img, index) => (
            <div
              key={img}
              className={`absolute inset-0 transition-opacity duration-1500 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={img}
                alt=""
                className="w-full h-full object-cover scale-105"
              />
            </div>
          ))}
        </div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />

        {/* Back Button - Floating */}
        <button
          onClick={onCancel}
          className="absolute top-6 left-6 z-20 p-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full hover:bg-white/20 transition-all duration-300"
          aria-label="Zurück"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>

        {/* Hero Content */}
        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center text-white">
          {/* Badge */}
          <div className="mb-6 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full animate-fade-in">
            <span className="text-sm font-medium text-white/90">Ort vorschlagen</span>
          </div>

          {/* Main Headlines */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-3 drop-shadow-lg animate-fade-in">
            Nicht jeder Ort ist ein
          </h1>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-amber-400 mb-6 drop-shadow-lg animate-fade-in">
            GoodPlace.
          </h2>
          <p className="text-xl sm:text-2xl text-white/90 mb-2 drop-shadow animate-fade-in font-light">
            Aber du kennst bestimmt einen.
          </p>

          {/* Subtext */}
          <p className="text-lg text-white/70 max-w-xl mx-auto mt-4 leading-relaxed animate-fade-in">
            Orte, an denen du einfach sein kannst. Ohne Rolle, ohne Druck, ohne Grund.
          </p>

          {/* CTA Button - Primary */}
          <button
            onClick={scrollToContent}
            className="mt-10 inline-flex items-center gap-3 px-8 py-4
                       bg-white/15 backdrop-blur-md border border-white/30
                       rounded-full text-white font-semibold
                       hover:bg-white/25 hover:border-white/50 hover:scale-105
                       transition-all duration-300 shadow-xl animate-slide-up"
          >
            Erfahre mehr
            <ChevronDown className="w-5 h-5 animate-bounce" />
          </button>
        </div>

        {/* Image Indicators */}
        <div className="absolute bottom-8 right-8 flex gap-2">
          {HERO_IMAGES.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentImageIndex
                  ? 'bg-white w-6'
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Bild ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* CONTENT SECTION - Mit sanftem Hintergrund */}
      <section className="relative bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 py-16 sm:py-24">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Frag dich: Warum fühlt sich dieser Ort besonders an?
            </h3>
            <p className="text-white/60 text-lg">
              Ein GoodPlace erfüllt mindestens eine dieser Fragen
            </p>
          </div>

          {/* 3 KERNFRAGEN - Glassmorphism Cards */}
          <div className="grid gap-6 sm:grid-cols-3 mb-16">
            {CORE_QUESTIONS.map((question, index) => (
              <div
                key={index}
                className="group p-6 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl
                           hover:bg-white/10 hover:border-white/20 hover:scale-105
                           transition-all duration-300"
              >
                {/* Emoji */}
                <div className="text-4xl mb-4">{question.emoji}</div>

                {/* Title */}
                <h4 className="font-semibold text-white text-lg mb-2 leading-snug">
                  {question.title}
                </h4>

                {/* Description */}
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  {question.description}
                </p>

                {/* Tag */}
                <span className="inline-block px-3 py-1 bg-white/10 rounded-full text-xs text-amber-400 font-medium">
                  {question.tag}
                </span>
              </div>
            ))}
          </div>

          {/* Confirmation Text */}
          <div className="text-center mb-16">
            <div className="inline-block px-6 py-4 bg-amber-500/10 border border-amber-500/20 rounded-2xl">
              <p className="text-white text-lg">
                Wenn du bei einer dieser Fragen{' '}
                <span className="font-bold text-amber-400">"Ja"</span> denkst –
                <br />
                dann hast du vermutlich einen GoodPlace gefunden.
              </p>
            </div>
          </div>

          {/* BEISPIELE Section */}
          <div className="mb-16">
            <h3 className="text-xl font-semibold text-white text-center mb-8">
              Was andere geteilt haben
            </h3>

            <div className="grid gap-6 sm:grid-cols-3">
              {EXAMPLE_PLACES.map((place, index) => {
                const Icon = place.icon;
                return (
                  <div
                    key={index}
                    className="group relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-md border border-white/10
                               hover:border-white/20 transition-all duration-300"
                  >
                    {/* Image */}
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={place.image}
                        alt={place.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    </div>

                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4 text-amber-400" />
                        <span className="font-medium text-white text-sm">
                          {place.name}
                        </span>
                      </div>
                      <p className="text-white/80 text-xs leading-relaxed italic line-clamp-2">
                        "{place.goodFactor}"
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* FINAL CTA Section */}
          <div className="text-center">
            <p className="text-white/50 mb-6 text-sm uppercase tracking-wide">Bereit?</p>

            <button
              onClick={onContinue}
              className="inline-flex items-center gap-3 px-10 py-5
                         bg-gradient-to-r from-amber-500 to-orange-500
                         hover:from-amber-400 hover:to-orange-400
                         text-white font-bold text-lg rounded-full
                         shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40
                         hover:scale-105 transition-all duration-300"
            >
              Meinen Ort vorschlagen
              <ArrowRight className="w-6 h-6" />
            </button>

            <p className="text-white/40 mt-6 text-sm">
              Dauert etwa 5 Minuten. Dein Vorschlag wird von unserem Team geprüft.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PlaceWizardIntro;
